/**
 * Сервис для работы с публичными ключами сотрудников
 * Поддерживает двустороннюю синхронизацию полных данных сотрудников
 */
import { apiClient } from './apiClient'
import { EmployeesRepo, type EmployeePublicKey, type EmployeeFullData } from '../offline/employeesRepo'
import { LogsRepo } from '../offline/logsRepo'
import { useDeviceStore } from '../store/deviceStore'
import { NetworkManager } from './networkManager'

export class EmployeesService {
  // Флаг для предотвращения параллельных синхронизаций (КРИТИЧНО для стабильности!)
  private static isSyncing = false
  private static lastSyncTime: Date | null = null
  
  // Публичные getters для диагностики
  static get syncInProgress(): boolean {
    return this.isSyncing
  }
  
  static get lastSync(): Date | null {
    return this.lastSyncTime
  }
  /**
   * Получает публичные ключи сотрудников для объекта через API
   * и сохраняет их в локальный кеш
   */
  /**
   * Получает актуальный site_id устройства с бэкенда
   */
  static async getDeviceSiteId(): Promise<string | null> {
    try {
      const deviceStore = useDeviceStore.getState()
      
      if (!deviceStore.deviceId || !deviceStore.deviceSecret) {
        return null
      }

      apiClient.setDeviceCredentials(deviceStore.deviceId, deviceStore.deviceSecret)
      
      // Запрашиваем информацию об устройстве с бэкенда
      const deviceInfo = await apiClient.get<{ device_id: string; site_id: string; name: string }>(
        '/tablet/device/info'
      )
      
      if (deviceInfo.site_id) {
        console.log(`[EmployeesService] ✅ Got device site_id from backend: ${deviceInfo.site_id}`)
        
        // Обновляем site_id в store если он отличается
        if (deviceInfo.site_id !== deviceStore.siteId) {
          console.log(`[EmployeesService] 🔄 Updating site_id in store: ${deviceStore.siteId} → ${deviceInfo.site_id}`)
          deviceStore.setSiteId(deviceInfo.site_id)
        }
        
        return deviceInfo.site_id
      }
      
      return deviceStore.siteId
    } catch (error) {
      console.error('[EmployeesService] ❌ Failed to get device site_id:', error)
      // Fallback на store
      return useDeviceStore.getState().siteId
    }
  }

  /**
   * Получает полные данные сотрудников через API и сохраняет в локальный кеш (двусторонняя синхронизация)
   * ЗАЩИТА: Предотвращает параллельные вызовы для избежания race condition
   */
  static async fetchAndCacheFullData(siteId?: string): Promise<EmployeeFullData[]> {
    // 🚨 КРИТИЧНО: Предотвращаем параллельные синхронизации
    // Race condition может привести к частичному сохранению данных!
    if (this.isSyncing) {
      console.log('[EmployeesService] ⏸️ Sync already in progress, skipping to prevent race condition')
      // Возвращаем текущие данные из кеша
      return await EmployeesRepo.getAllFullData()
    }
    
    this.isSyncing = true
    
    try {
      const deviceStore = useDeviceStore.getState()
      
      // Проверяем, что устройство зарегистрировано
      if (!deviceStore.deviceId || !deviceStore.deviceSecret) {
        console.error('[EmployeesService] ❌ Device not registered:', { deviceId: deviceStore.deviceId, hasSecret: !!deviceStore.deviceSecret })
        throw new Error('Device not registered')
      }

      // Если siteId не передан, получаем актуальный с бэкенда
      let actualSiteId: string | undefined = siteId
      if (!actualSiteId) {
        const backendSiteId = await this.getDeviceSiteId()
        if (!backendSiteId) {
          throw new Error('Site ID not available')
        }
        actualSiteId = backendSiteId
      }

      // Проверяем статус сети - если офлайн, не пытаемся синхронизировать
      const networkStatus = NetworkManager.getStatus()
      if (!NetworkManager.isOnline()) {
        // Тихо возвращаемся без логирования ошибки в офлайне
        console.log(`[EmployeesService] ⏸️ Network offline (${networkStatus}), skipping sync for site: ${actualSiteId}`)
        // Возвращаем закешированные данные
        const cached = await EmployeesRepo.getAllFullData()
        return cached
      }

      console.log(`[EmployeesService] 🔄 Starting fetchAndCacheFullData for site: ${actualSiteId} (network: ${networkStatus})`)

      // Валидация формата UUID для site_id
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      if (!uuidRegex.test(actualSiteId)) {
        console.error(`[EmployeesService] ❌ Invalid site_id format: ${actualSiteId}`)
        throw new Error(`Некорректный формат site_id: "${actualSiteId}". Ожидается UUID формат.`)
      }

      // Устанавливаем credentials для API клиента
      apiClient.setDeviceCredentials(deviceStore.deviceId, deviceStore.deviceSecret)

      const apiUrl = `/tablet/employees/full-data?site_id=${actualSiteId}`
      console.log(`[EmployeesService] 📡 Requesting: ${apiUrl}`)

      // Получаем полные данные через API
      const fullData = await apiClient.get<EmployeeFullData[]>(apiUrl)

      console.log(`[EmployeesService] ✅ Received ${fullData.length} employees from API`)

      // Сохраняем в локальный кеш
      await EmployeesRepo.saveFullDataBatch(fullData)

      // Удаляем сотрудников, которых больше нет на сервере
      const serverEmployeeIds = fullData.map(emp => emp.employee_id)
      const deletedCount = await EmployeesRepo.syncWithServer(serverEmployeeIds)
      if (deletedCount > 0) {
        console.log(`[EmployeesService] 🗑️ Removed ${deletedCount} deleted employees`)
      }

      await LogsRepo.save('info', `Fetched ${fullData.length} employees for site ${actualSiteId}`)

      return fullData
    } catch (error) {
      // Проверяем, является ли ошибка сетевой
      const isNetworkError = error instanceof Error && 
        (error.name === 'NetworkError' || 
         error.message.includes('Failed to fetch') ||
         error.message.includes('интернету'))

      // Если сетевая ошибка в офлайне, НЕ логируем как ошибку
      if (isNetworkError && !NetworkManager.isOnline()) {
        console.log('[EmployeesService] ⏸️ Network offline, returning cached data')
        // Возвращаем закешированные данные
        const cached = await EmployeesRepo.getAllFullData()
        return cached
      }

      // Для других ошибок логируем
      const errorMsg = error instanceof Error ? error.message : 'Failed to fetch full employee data'
      console.error('[EmployeesService] ❌ fetchAndCacheFullData failed:', error)
      await LogsRepo.save('error', `Failed to fetch full employee data: ${errorMsg}`, {
        site_id: siteId || 'not provided',
        error: errorMsg,
      })
      
      // Возвращаем закешированные данные вместо выброса ошибки
      const cached = await EmployeesRepo.getAllFullData()
      return cached
    } finally {
      // 🚨 КРИТИЧНО: Всегда сбрасываем флаг, даже при ошибке!
      this.isSyncing = false
      this.lastSyncTime = new Date()
    }
  }

  /**
   * Получает публичные ключи сотрудников через API (legacy, предпочтительнее использовать fetchAndCacheFullData)
   */
  static async fetchAndCachePublicKeys(siteId?: string): Promise<EmployeePublicKey[]> {
    try {
      const deviceStore = useDeviceStore.getState()
      
      // Проверяем, что устройство зарегистрировано
      if (!deviceStore.deviceId || !deviceStore.deviceSecret) {
        console.error('[EmployeesService] ❌ Device not registered:', { deviceId: deviceStore.deviceId, hasSecret: !!deviceStore.deviceSecret })
        throw new Error('Device not registered')
      }

      // Если siteId не передан, получаем актуальный с бэкенда
      let actualSiteId: string | undefined = siteId
      if (!actualSiteId) {
        const backendSiteId = await this.getDeviceSiteId()
        if (!backendSiteId) {
          throw new Error('Site ID not available')
        }
        actualSiteId = backendSiteId
      }

      // Валидация формата UUID для site_id
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
      if (!uuidRegex.test(actualSiteId)) {
        console.error(`[EmployeesService] ❌ Invalid site_id format: ${actualSiteId}`)
        throw new Error(`Некорректный формат site_id: "${actualSiteId}". Ожидается UUID формат.`)
      }

      // Устанавливаем credentials для API клиента
      apiClient.setDeviceCredentials(deviceStore.deviceId, deviceStore.deviceSecret)

      const apiUrl = `/tablet/employees/public-keys?site_id=${actualSiteId}`
      console.log(`[EmployeesService] 📡 Requesting: ${apiUrl}`)

      // Получаем публичные ключи через API
      const publicKeys = await apiClient.get<EmployeePublicKey[]>(apiUrl)

      console.log(`[EmployeesService] ✅ Received ${publicKeys.length} public keys from API`)

      // Сохраняем в локальный кеш
      await EmployeesRepo.savePublicKeysBatch(publicKeys)

      await LogsRepo.save('info', `Fetched ${publicKeys.length} public keys for site ${actualSiteId}`)

      return publicKeys
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to fetch public keys'
      console.error('[EmployeesService] ❌ fetchAndCachePublicKeys failed:', error)
      console.error('[EmployeesService] ❌ Error details:', {
        message: errorMsg,
        siteId: siteId || 'not provided',
        stack: error instanceof Error ? error.stack : undefined
      })
      await LogsRepo.save('error', `Failed to fetch public keys: ${errorMsg}`, {
        site_id: siteId || 'not provided',
        error: errorMsg,
      })
      throw error
    }
  }

  /**
   * Получает публичный ключ сотрудника (из кеша или через API)
   */
  static async getPublicKey(
    employeeId: string,
    siteId: string
  ): Promise<string | null> {
    // Сначала проверяем локальный кеш
    let publicKey = await EmployeesRepo.getPublicKey(employeeId)
    if (publicKey) return publicKey

    // Если ключа нет в кеше, пытаемся получить через API
    try {
      await this.fetchAndCachePublicKeys(siteId)
      publicKey = await EmployeesRepo.getPublicKey(employeeId)
      if (!publicKey) {
        const errorMsg = `Сотрудник ${employeeId} не найден для объекта ${siteId}`
        await LogsRepo.save('warn', errorMsg)
        throw new Error(errorMsg)
      }
      return publicKey
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to fetch public key'
      await LogsRepo.save('error', errorMsg)
      throw error
    }
  }

  /**
   * Предзагружает публичные ключи для объекта (для оптимизации)
   */
  static async preloadPublicKeys(siteId: string): Promise<void> {
    try {
      await this.fetchAndCachePublicKeys(siteId)
    } catch (error) {
      console.error('Failed to preload public keys:', error)
      // Не бросаем ошибку, так как это предзагрузка
    }
  }

  /**
   * Запускает периодическую синхронизацию сотрудников (двусторонняя синхронизация с полными данными)
   * @param siteId - ID объекта
   * @param intervalSeconds - Интервал синхронизации в секундах (по умолчанию 60)
   * @returns Функция для остановки синхронизации
   */
  static startPeriodicSync(siteId: string, intervalSeconds: number = 60): () => void {
    console.log(`[EmployeesService] 🚀 Starting periodic full data sync every ${intervalSeconds}s for site ${siteId}`)
    
    // Сразу загружаем данные
    this.fetchAndCacheFullData(siteId)
      .then(data => {
        console.log(`[EmployeesService] ✅ Initial sync: ${data.length} employees`)
      })
      .catch(error => {
        if (NetworkManager.isOnline()) {
          console.error('[EmployeesService] ❌ Initial sync FAILED:', error)
        }
      })
    
    // Запускаем периодическую синхронизацию
    const intervalId = setInterval(async () => {
      // Проверяем сеть перед попыткой синхронизации
      if (!NetworkManager.isOnline()) {
        // Не логируем в офлайне
        return
      }
      
      try {
        console.log('[EmployeesService] 🔄 Running periodic sync...')
        const data = await this.fetchAndCacheFullData(siteId)
        console.log(`[EmployeesService] ✅ Periodic sync SUCCESS: ${data.length} employees`)
      } catch (error) {
        // Не логируем сетевые ошибки
        if (NetworkManager.isOnline()) {
        console.error('[EmployeesService] ❌ Periodic sync FAILED:', error)
        }
      }
    }, intervalSeconds * 1000)
    
    // Возвращаем функцию для остановки
    return () => {
      console.log('[EmployeesService] 🛑 Stopping periodic sync')
      clearInterval(intervalId)
    }
  }
}

